import { Component, HostListener, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { config } from '../../../../config/config';
import {
  VisitorCalendarActionCommand,
  VisitorSettingsCommand,
  VisitorStyleCommand,
} from '../../../commands/pagination.query.command';
import { ButtonTriggerEvent } from '../../../common/components';
import { BaseComponent } from '../../../common/components/base.component';
import { PageSpinnerComponent } from '../../../common/components/page.spinner/page.spinner.component';
import {
  WaterFallContainerComponent
} from '../../../common/components/water.fall.container/water.fall.container.component';
import { localStorageKeys } from '../../../common/constants/local.storage.keys';
import { clone } from '../../../common/extensions';
import { AuthService } from '../../../common/services/auth.service';
import { ModalService } from '../../../common/services/modal.service';
import { EditorChoiceDataSource, HubEvent } from '../../../models/hub/hub.event';
import { HubPlace } from '../../../models/hub/hub.place';
import { CustomizationApiService } from '../../../services/customization.api.service';
import { VisitorApiService } from '../../../services/visitor.api.service';
import { EditorChoiceStore, HubCalendarEventsStore } from '../../../stores/hub.calendar.store';
import {
  AllFrontendSettingsStore,
  VisitorCalendarStore,
  VisitorDataCache,
  VisitorSettingsStore,
} from '../../../stores/visitor.calendar.store';
import { EventDetailsModalComponent } from '../event.details.modal/event.details.modal.component';
import { EventDetailsModalActions } from '../event.details.modal/event.details.modal.provider';
import { PlaceDetailsModalComponent } from '../place.details.modal/place.details.modal.component';
import { PlaceDetailsModalActions } from '../place.details.modal/place.details.modal.provider';
import { TripDetailModalActions, TripDetailModalData } from '../trip/trip.detail.modal/trip.detail.modal.provider';
import { TripDetailModalComponent } from '../trip/trip.detail.modal/trip.detail.modal.component';
import { AllFrontendSettingsCommand } from '../../../commands/visitor/all.frontend.settings.command';

@Component({
  selector: 'ig-calendar',
  templateUrl: './visitor.calendar.component.html',
  styleUrls: ['./visitor.calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VisitorCalendarComponent extends BaseComponent implements OnInit {
  public hubName: string;
  public customEventBtnId: string;
  public customPlaceBtnId: string;
  public category: string;
  public events: HubEvent[];
  public places: HubPlace[];
  public actionCommand: VisitorCalendarActionCommand;
  public settingsCommand = new VisitorSettingsCommand();
  public isAllDataLoaded: boolean;
  public isLoading: boolean;
  public isNoContent = false;
  public isContentLoaded = false;
  public isCalendarStyleLoaded = false;
  public calendarCardWidth = config.calendarCardDefaultWidth;
  public maxColumn = 6;
  public placeDetailsId: string;
  public eventDetailsId: string;
  public frontendSettings: AllFrontendSettingsCommand;
  @ViewChild('waterFallContainer') public waterFallContainer: WaterFallContainerComponent;
  @ViewChild('spinner') public spinner: PageSpinnerComponent;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    public modalService: ModalService,
    public editorChoiceStore: EditorChoiceStore,
    private customizationApiService: CustomizationApiService,
    private visitorCalendarStore: VisitorCalendarStore,
    private allFrontendSettingsStore: AllFrontendSettingsStore,
    private visitorSettingsStore: VisitorSettingsStore,
    private visitorApiService: VisitorApiService
  ) {
    super({activatedRoute});
  }

  public ngOnInit() {
    let {hubName, category, customEventBtnId, customPlaceBtnId, placeId, eventId, categoryList, menuType} =
      this.allRouteParams;
    this.hubName = hubName;
    this.customEventBtnId = customEventBtnId;
    this.customPlaceBtnId = customPlaceBtnId;
    this.category = category;
    this.placeDetailsId = placeId;
    this.eventDetailsId = eventId;

    this.processScriptSolution();

    /**
     * take the all frontend custom settings from visitor.component
     */
    this.allFrontendSettingsStore.allFrontendSettingsCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: AllFrontendSettingsCommand) => {
        console.log('frontend settings get in store');
        this.frontendSettings = command;
        this.editorChoiceStore.infoCommand.next(this.frontendSettings.customSettings.customEditorChoice);
        this.isCalendarStyleLoaded = true;
      });

    /**
     * take the action command from visitor.side.bar or top.bar and get corresponding data (event/places)
     */
    this.visitorCalendarStore.actionCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: VisitorCalendarActionCommand) => {
        console.log('command got', command);
        if (!!this.actionCommand && this.actionCommand.isEventCalendar !== command.isEventCalendar) {
          return;
        }

        // visitor.components.ts uses this to handle browser's back button event
        localStorage.setItem(localStorageKeys.eventQueryCommand, JSON.stringify(command));
        console.log('command set', command);

        this.showSpinner();

        // this.customEventBtnId || this.customPlaceBtnId means directly visit the custom button page, command.queryCommand.isCustom means click the custom button in the dropdown
        if ((this.customEventBtnId || this.customPlaceBtnId) && !command.queryCommand.isCustom) {
          this.loadCustomBtns(command);
        } else {
          this.loadItems(command);
        }
      });
  }

  private processScriptSolution() {
    /** for clients who use the script solution (embed Imgoing script into their website)
     if a place name + Id is specified (by imgoing-place), then go to the place details page after load
     */
    if (this.isScriptSolution) {
      if (window.location.search) {
        console.log(window.location.search);
        if (window.location.search.indexOf('imgoing-place=') >= 0) {
          let nameId = window.location.search.split('imgoing-place=')[1].split('&')[0];
          if (nameId.indexOf('-') >= 0) {
            let nameIdSeg = nameId.split('-');
            this.placeDetailsId = nameIdSeg[nameIdSeg.length - 1];
            console.log(`this.placeDetailsId: ${this.placeDetailsId}`);
          } else {
            console.log(`nameId not processed: ${nameId}`);
          }
        }
        if (window.location.search.indexOf('imgoing-event=') >= 0) {
          let nameId = window.location.search.split('imgoing-event=')[1].split('&')[0];
          if (nameId.indexOf('-') >= 0) {
            let nameIdSeg = nameId.split('-');
            this.eventDetailsId = nameIdSeg[nameIdSeg.length - 1];
            console.log(`this.eventDetailsId: ${this.eventDetailsId}`);
          } else {
            console.log(`nameId not processed: ${nameId}`);
          }
        }
      }
    }
  }

  public loadMore() {
    this.actionCommand.queryCommand.page++;
    if (this.actionCommand.isEventCalendar) {
      this.getEvents(true);
    } else {
      this.getPlaces(true);
    }
  }

  public loadCustomBtns(command) {
    if (this.customEventBtnId) {
      this.customizationApiService.getCustomEvent(this.hubName).subscribe((custom) => {
        let customBtn = custom.customEvents.first((x) => x._id.toString() === this.customEventBtnId);
        if (customBtn) {
          command.queryCommand.isCustom = true;
          command.queryCommand.selectedEvents = customBtn.events;
          this.loadItems(command, 'event');
        } else {
          alert('Wrong custom event button Id, please check!');
        }
      });
    } else if (this.customPlaceBtnId) {
      // this.customizationApiService.getCustomEvent(this.hubName).subscribe((custom) => {
      //     let customBtn = custom.customPlaces.first((x) => x._id.toString() === this.customPlaceBtnId);
      //     if (customBtn) {
      //         command.queryCommand.isCustom = true;
      //         command.queryCommand.type = this.customPlaceBtnId;
      //         command.queryCommand.selectedPlaces = null;
      //         this.loadItems(command, 'place');
      //     } else {
      //         alert('Wrong custom place button Id, please check!');
      //     }
      // });
      command.queryCommand.isCustom = true;
      command.queryCommand.type = this.customPlaceBtnId;
      command.queryCommand.selectedPlaces = null;
      this.loadItems(command, 'place');
    }
  }

  public getEvents(isLoadMore = false) {
    this.isAllDataLoaded = false;
    let loadEvents = () => {
      this.visitorApiService.getEvents(this.hubName, this.actionCommand.queryCommand).subscribe((data) => {
        this.processCalendarStyle();
        this.afterGotEvents(isLoadMore, data);
      });
    };
    try {
      loadEvents();
    } catch (e) {
      console.error(e);
    } finally {
      this.hideSpinner();
      // setTimeout(() => {
      //     // on Safari, sometimes the events cannot be loaded in the first time
      //     if (this.isMobile && isSafari()) {
      //         loadEvents();
      //     }
      // });
    }
  }

  public getPlaces(isLoadMore = false) {
    this.isAllDataLoaded = false;
    let loadPlaces = () => {
      // for map view
      localStorage.setItem(localStorageKeys.selectedPlaceCategory, this.actionCommand.queryCommand.category);

      this.actionCommand.queryCommand.source = this.frontendSettings.dataSource
        ? this.frontendSettings.dataSource.place
        : 'facebook';
      this.visitorApiService.getPlaces(this.hubName, this.actionCommand.queryCommand).subscribe((data) => {
        this.processCalendarStyle();
        this.afterGotPlaces(isLoadMore, data);
      });
    };

    try {
      loadPlaces();
    } catch (e) {
      console.error(e);
    } finally {
      this.hideSpinner();
      // setTimeout(() => {
      //     if (this.isMobile && isSafari()) {
      //         loadPlaces();
      //     }
      // });
    }
  }

  private loadItems(command, type?) {
    if (type) {
      command.isEventCalendar = type === 'event';
    }
    this.actionCommand = clone(command);
    this.isNoContent = false;
    this.waterFallContainer && this.waterFallContainer.startLoading();

    setTimeout(() => {
      console.log('this.frontendSettings', this.frontendSettings);
      if (!this.frontendSettings) {
        this.getCustomerSettings().subscribe((settings) => {
          this.frontendSettings = settings;
          this.isCalendarStyleLoaded = true;
          command.isEventCalendar ? this.getEvents() : this.getPlaces();
        });
      } else {
        command.isEventCalendar ? this.getEvents() : this.getPlaces();
      }
    }, 100);
  }

  private getCustomerSettings(): Observable<any> {
    return Observable.create((observable) => {
      try {
        this.customizationApiService.getAllFrontendSettings(this.hubName).subscribe((settings) => {
          if (!settings) return;
          try {
            this.setCustomerColor(settings.customSettings.Style);
            observable.next(settings);
            observable.complete();
          } catch (e) {
            observable.error(e);
          }
        });
      } catch (e) {
        observable.error(e);
      }
    });
  }

  private afterGotEvents(isLoadMore: boolean, data) {
    if (
      !isLoadMore &&
      !this.frontendSettings.customSettings.menuStyle.isSideBar &&
      !this.frontendSettings.customSettings.visitorPromotion.isDisabled &&
      this.frontendSettings.customSettings.visitorPromotion.image
    ) {
      let promotion = new HubEvent();
      promotion.isPromotionImage = true;
      promotion.cover = {source: '', isDefault: false};
      promotion.cover.source = this.frontendSettings.customSettings.visitorPromotion.image.toString();
      promotion.pinnedTime = new Date();
      promotion.promotionLink = this.frontendSettings.customSettings.visitorPromotion.link.toString();
      data.unshift(promotion);
    }

    if (!isLoadMore) {
      this.events = data;
    } else {
      // for mobile view safari, as it loads twice to avoid not loading issue, so here is to filter duplicated items
      const filteredData = data.filter((x) => !this.events.any((y) => y._id === x._id));
      this.events = this.events.concat(filteredData);
    }
    this.isContentLoaded = true;
    // localStorage.setItem(localStorageKeys.visitorEvents, JSON.stringify(this.events));
    VisitorDataCache.events = this.events;

    if (data.length === 0) {
      this.isAllDataLoaded = true;
    }
    this.waterFallContainer.reLayout();

    this.checkIsNoContent();

    if (this.eventDetailsId && !isLoadMore) {
      let event = this.events.first((x) => x._id === this.eventDetailsId);
      if (event) {
        event.hubName = this.hubName;
      }
      let actions = <EventDetailsModalActions>{
        confirm: (event: ButtonTriggerEvent, data: any) => {
        },
      };

      if (this.isMobile) {
        this.showDetailModalForMobile(event || {eventId: this.eventDetailsId}, 'event');
      } else {
        this.modalService.show({
          component: EventDetailsModalComponent,
          providers: [
            {provide: EventDetailsModalActions, useValue: actions},
            {provide: HubEvent, useValue: event || {hubName: this.hubName, _id: this.eventDetailsId}},
          ],
          panelClass: 'modal-fixed-top-lower',
        });
      }
    }
    console.log('events loaded');
    this.customEventBtnId = null;
    this.customPlaceBtnId = null;
    !isLoadMore && this.hideSpinner();
  }

  private afterGotPlaces(isLoadMore, data) {
    if (
      !isLoadMore &&
      !this.frontendSettings.customSettings.menuStyle.isSideBar &&
      !this.frontendSettings.customSettings.visitorPromotion.isDisabled &&
      this.frontendSettings.customSettings.visitorPromotion.image
    ) {
      if (!config.hidePromotionImageForPlaces.any((x) => x.toLocaleLowerCase() === this.hubName.toLowerCase())) {
        if (this.frontendSettings.customSettings.calendarStyle.place !== 'tile') {
          let promotion = new HubPlace();
          promotion.isPromotionImage = true;
          promotion.cover = {
            source: this.frontendSettings.customSettings.visitorPromotion.image,
            isDefault: false,
          };
          promotion.pinnedTime = new Date();
          promotion.promotionLink = this.frontendSettings.customSettings.visitorPromotion.link;
          data.unshift(promotion);
        }
      }
    }

    if (!isLoadMore) {
      this.places = data;
    } else {
      this.places = this.places.concat(data);
    }
    this.isContentLoaded = true;
    localStorage.setItem(localStorageKeys.visitorPlaces, JSON.stringify(this.places));

    if (data.length === 0) {
      this.isAllDataLoaded = true;
    }
    this.waterFallContainer.reLayout();

    this.checkIsNoContent();
    if (this.placeDetailsId && !isLoadMore) {
      let place = this.places.first((x) => x._id === this.placeDetailsId);
      //if (place) {
      let actions = <PlaceDetailsModalActions>{
        confirm: (event: ButtonTriggerEvent, data: any) => {
        },
      };

      if (this.isMobile) {
        this.showDetailModalForMobile(place || {placeId: this.placeDetailsId}, 'place');
      } else {
        this.modalService.show({
          component: PlaceDetailsModalComponent,
          providers: [
            {provide: PlaceDetailsModalActions, useValue: actions},
            {
              provide: HubPlace,
              useValue: place || {hubName: this.hubName, placeId: this.placeDetailsId},
            },
          ],
          panelClass: 'modal-fixed-top',
        });
      }
    }

    !isLoadMore && this.hideSpinner();
  }

  public showDetailModalForMobile(item: any, displayingOn: string, isShowAddToTripBtn = false, index?: number) {
    let actions = <TripDetailModalActions>{
      confirm: (event: ButtonTriggerEvent) => {
      },
    };
    this.modalService.show({
      component: TripDetailModalComponent,
      providers: [
        {provide: TripDetailModalActions, useValue: actions},
        {
          provide: TripDetailModalData,
          useValue: new TripDetailModalData(
            item,
            displayingOn,
            this.hubName,
            null,
            null,
            isShowAddToTripBtn,
            index
          ),
        },
      ],
      panelClass: ['width-modal-100p', 'ig-trip-info-modal'],
    });
  }

  private setCustomerColor(data) {
    if (!data) {
      return;
    }
    let head = document.getElementsByTagName('head')[0];
    let s = document.createElement('style');
    s.setAttribute('type', 'text/css');
    let css = '';
    if (data.defaultButton.backgroundColor) {
      css += `--default-btn-background-color: ${data.defaultButton.backgroundColor};`;
    }
    if (data.eventButton.backgroundColor) {
      css += `--event-btn-background-color: ${data.eventButton.backgroundColor};`;
    }
    if (data.placeButton.backgroundColor) {
      css += `--place-btn-background-color: ${data.placeButton.backgroundColor};`;
    }
    if (data.pinnedEventBorder.borderColor) {
      css += `--pinned-event-border-color: ${data.pinnedEventBorder.borderColor};`;
    }
    if (data.detailButton.backgroundColor) {
      css += `--detail-btn-background-color: ${data.detailButton.backgroundColor};`;
    }
    s.appendChild(
      document.createTextNode(`
                :root {
                  ${css}
                }
            `)
    );
    head.appendChild(s);
    // setTimeout(() => {
    //     this.spinner.hide();
    // });
  }

  private checkIsNoContent() {
    if ((!this.events || !this.events.length) && (!this.places || !this.places.length)) {
      this.isNoContent = true;
    }

    // if ((this.events.length === 1 && this.events[0].isPromotionImage) || (this.places.length === 1 && this.places[0].isPromotionImage)) {
    //     this.isNoContent = true;
    // }
  }

  private processCalendarStyle() {
    if (this.actionCommand.isEventCalendar) {
      if (this.frontendSettings.customSettings.calendarStyle.event === 'square') {
        this.calendarCardWidth = config.calendarCardSquareWidth;
      } else if (this.frontendSettings.customSettings.calendarStyle.event === 'long') {
        this.calendarCardWidth = config.calendarCardLongWidth;
        this.maxColumn = 4;
      }
    } else {
      if (
        this.frontendSettings.customSettings.calendarStyle.place === 'square' ||
        this.frontendSettings.customSettings.calendarStyle.place === 'tile'
      ) {
        this.calendarCardWidth = config.calendarCardSquareWidth;
      } else if (this.frontendSettings.customSettings.calendarStyle.place === 'long') {
        this.calendarCardWidth = config.calendarCardLongWidth;
        this.maxColumn = 4;
      }
    }
  }

  private showSpinner() {
    this.spinner.show();
  }

  private hideSpinner() {
    setTimeout(() => {
      this.spinner.hide();
    });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/components/visitor/calendar/visitor.calendar.component.ts